import { afterNextRender, afterRenderEffect, ChangeDetectionStrategy, Component, computed, CUSTOM_ELEMENTS_SCHEMA, ElementRef, inject, input, signal, untracked, viewChild, viewChildren } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { filter, switchMap, combineLatest, debounceTime } from 'rxjs';
import { MetaService } from '../../meta.service';
import { AuthService, CacheService } from '../../auth.service';
import { IDay, Offer, splitBitwise, weekdaysAsBitwise } from '../../types';
import { TitleCasePipe } from '@angular/common';
import { MatDivider } from '@angular/material/divider';
import { MatIcon } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatTabsModule } from '@angular/material/tabs';
import { BucketPipe } from '../../bucket.pipe';
import { NgScrollbarModule } from 'ngx-scrollbar';
import { GoogleMap, GoogleMapsModule, MapInfoWindow } from '@angular/google-maps';
import { JoinPipe } from '../../join.pipe';
import { DateTime } from 'luxon';
import { ImageFallbackDirective } from '../../image-fallback.directive';
import { PaintDirective } from '../../paint.directive';
import { backend } from '../../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { MatButtonModule } from '@angular/material/button';
import { SwiperContainer } from 'swiper/element';
import { SwiperOptions } from 'swiper/types';
import { ToastrService } from 'ngx-toastr';
import { OfferLoyaltyCardComponent } from '../offer-loyalty-card/offer-loyalty-card.component';
import { HumanizePipe } from "../../humanize.pipe";

@Component({
  selector: 'cloudigo-offer-page',
  standalone: true,
  imports: [
    MatIcon,
    RouterModule,
    BucketPipe,
    MatTabsModule,
    MatDivider,
    MatListModule,
    NgScrollbarModule,
    GoogleMapsModule,
    JoinPipe,
    ImageFallbackDirective,
    PaintDirective,
    MatButtonModule,
    OfferLoyaltyCardComponent,
    HumanizePipe
  ],
  templateUrl: './offer-page.component.html',
  styleUrl: './offer-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[style.--offer-fill-color]': 'planColors()?.background || "#0075ff"',
    '[style.--offer-text-color]': 'planColors()?.foreground || "#ffffff"',
  },
  schemas: [
    CUSTOM_ELEMENTS_SCHEMA
  ]
})
export default class OfferPageComponent {
  cache = inject(CacheService);
  meta = inject(MetaService);
  route = inject(ActivatedRoute);
  http = inject(HttpClient);
  auth = inject(AuthService);
  toast = inject(ToastrService);

  LatLngBounds?: typeof google.maps.LatLngBounds;
  map = viewChild<GoogleMap>(GoogleMap);
  infoWindows = viewChildren(MapInfoWindow);
  swiper = viewChild<ElementRef<SwiperContainer>>('swiper');
  
  slug = input.required<string>();
  url = input.required<string>();
  offer = signal<Offer|null>(null);

  iconSize = { width: 45, height: 45, equals: (v: any) => true };
  infoOffset = { width: 0, height: -45, equals: (v: any) => true };

  openInfo(storeInfo: MapInfoWindow){

    const windows = this.infoWindows();

    if(windows && windows.length){
      windows.forEach(infoWindow => {
        infoWindow.close();
      });
    }
    
    storeInfo?.open();
  }

  planColors = computed(() => this.offer()?.plan_details?.color_settings || this.cache.registeredPlans().find(plan => this.offer()?.plan == plan.identifier)?.color_settings);

  canUse = computed(() => {
    const { plan } = this.offer() || { plan: 'basic'};
    const userPlans = this.auth.userPlans();

    return userPlans.includes(plan);
  })


  cities = computed(() => {
    const stores = this.offer()?.stores || [];

    const cities = new Set<string>();
    stores.forEach(store => cities.add(store.location.city));

    return Array.from(cities).filter(c => !!c);
  });

  artifical_terms = computed(() => {
    const artificalTerms = [];

    const { start_time, end_time, stores, end_date, restrictions, userinterval, userlimit, userredeemed } = this.offer() ?? {};

    //TERM AVAILABLE TIME | START
    let availableTime = '';
    if(start_time && end_time)
    {
        availableTime = 'Valid between ' + start_time + ' and ' + end_time;
    }
    else if (start_time)
    {
        availableTime = 'Valid from ' + start_time + ' onwards';                    
    }
    else if (end_time)
    {
        availableTime = 'Valid until ' + end_time;
    }
    else{
      availableTime = 'Valid ' + (stores?.length ? 'during open hours' : 'any time');
    }

    artificalTerms.push({
      icon: 'schedule',
      text: availableTime
    });
    //TERM AVAILABLE TIME | END

    //TERM AVAILABLE DATE | START
    if (end_date)
    {
        artificalTerms.push({
          icon: 'event',
          text: 'Valid until the ' + DateTime.fromISO(end_date).toFormat('MMMM d, yyyy')
        });          
    }

    //TERM AVAILABLE DATE | END

    //TERM HOLIDAYS | START
    if(!restrictions?.holidays){
      artificalTerms.push({
        icon: 'event_busy',
        text: 'Not valid on Public Holidays' 
      });
    }
    //TERM HOLIDAYS | END

    //TERM AVAILABLE USES | START
    let availableUses = 'Unlimited uses';
    if(userlimit){
      availableUses = `${this.usage().replace('USE ', '').replace('ONCE', 'ONE TIME').replace('TIME', 'USE')} (${userredeemed || 0}/${userlimit} used)`.toLowerCase();
    }

    artificalTerms.push({
      icon: 'confirmation_number',
      text: availableUses[0].toUpperCase() + availableUses.slice(1)
    });

    return artificalTerms;
  });

  usage = computed(() => {
    const { userinterval, userlimit } = this.offer() || { userlimit: 1, userinterval: 'user' };

    if(!userlimit) return 'UNLIMITED USES';

    let text = '';

    if(userlimit == 1){
      text += 'USE ONCE';
    } else {
      text += `USE ${userlimit} TIMES`;
    }

    if(!userinterval || userinterval != 'user'){
      text += ` PER ${userinterval.toUpperCase()}`;
    }

    return text;
  });

  storeTimeslots = computed(() => {
    const stores = this.offer()?.stores || [];

    const list: { [key: string]: string[][] } = {};

    stores.forEach(store => {
      list[store._id] = [];

      for(let { key, value } of weekdaysAsBitwise){
        const dayslots: string[] = [];
        (store.days || []).filter(d => (d.day & value) == value).forEach(d => {
          dayslots.push(`${d.open || '00:00'} - ${d.close || '23:59'}`);
        });

        list[store._id].push([key, dayslots.length ? dayslots.join(', ') : 'Closed']);
      }
     });

     return list;
  });

  constructor(){

    combineLatest([toObservable(this.slug), toObservable(this.url)]).pipe(
      takeUntilDestroyed(),
      debounceTime(100),//to prevent double calls
      filter(([slug, url]) => !!slug && !slug.includes('.map') && !!url && !url.includes('.map')),
      switchMap(([slug,url]) => this.http.get<Offer>(`${backend}offer?url=${url}&slug=${slug}`, { 
        headers: { 'no-transfer': 'true' }
      }))
    ).subscribe({
      next: offer => {
        offer.categories?.forEach(category => {
          this.cache.registerIconFromSVG(category.slug, category.icon_svg);
        });
        if(offer.plan_details){
          this.cache.registerPlan(offer.plan_details);
        }
        this.offer.set(offer);
        this.meta.setTags(`${offer.short_title} from ${offer.brand.name} - cloudigo Malta`, `Download cloudigo now to start benefitting from ${offer.brand.name} Discounts - Discover hundreds of incredible discounts up to 50% OFF across Malta & Gozo. Start saving now!`, offer.gallery_urls?.at(0) || offer.brand.image_url || offer.brand.logo_url);
  
        this.centerMap();
      },
      error: error => {
        if(this.meta.isBrowser){
          this.toast.warning('Offer is invalid or no longer available', 'Offer not found');
        }
        this.meta.router.navigate(['/brands', this.slug()]);
      }
    });

    afterNextRender(() => {
      (google.maps.importLibrary("core") as Promise<google.maps.CoreLibrary>).then(({ LatLngBounds }: google.maps.CoreLibrary) => {
        this.LatLngBounds = LatLngBounds;
      }).catch(err => console.error('Failed to load GoogleMapsCore'));
    });

    afterRenderEffect(() => {
      const swiper = this.swiper();
      const offer = this.offer();

      untracked(() => {
        if(offer && swiper){
          const size = offer?.gallery_urls?.length ?? 0;
          const options: SwiperOptions = {
            autoplay: {
              delay: 5000
            },
            slidesPerView: 1,
            loop: size > 1,
            centerInsufficientSlides: true,
            breakpoints: {
              "640": { slidesPerView: 2, loop: size > 2 },
              "960": { slidesPerView: 3, loop: size > 3 }
            }
          };

          Object.assign(swiper.nativeElement, options);

          swiper.nativeElement.initialize();
        }
      });
    });
  }

  centerMap(): void {
    const offer = this.offer();
    if(this.LatLngBounds && offer){
      const area = new this.LatLngBounds();

      const stores = offer.stores || [];

      if(stores.length == 1){
        const store = stores.at(0)!;
        setTimeout(() => {
          this.map()?.panTo({ lat: store.location.geo.y, lng: store.location.geo.x })
        });
      } else if(stores.length) {
          stores.forEach(store => {
            area.extend({ lat: store.location.geo.y, lng: store.location.geo.x });
          });
    
          setTimeout(() => {
            this.map()?.fitBounds(area, 45);
          });
      }

    }
  }

  filterDays = (bit: number, days?: IDay[]) => {
    return days?.filter(d => splitBitwise(d.day || 0).includes(bit));
  }
}
